import { useAssignToAllPriceListsMutation, useGetProductPriceListQuery, useLazyGetProductPriceListAuditDetailsQuery } from "features/products/redux/productApiSlice";
import { PRODUCT_PRICE_LIST_TAX_LOV_TYPES, PRODUCT_REFRESH_PRICE_LIST_TYPE_LOV_TYPES } from "features/products/types/LOVTypes";
import { ProductPriceListItem } from "features/products/types/productPriceListTypes";
import { useLovData } from "hooks/useLovData";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

export const usePriceListData = (selectedProductRowId: number, isActive: boolean) => {

  const [initialUpdatedTimestamps, setInitialUpdatedTimestamps] = useState<{ [rowId: number]: string }>({});


  //Redux Query for product price list
  const {
    data: productPriceListData,
    isLoading: isProductPriceListLoading,
    isFetching: isProductPriceListFetching,
    error: productPriceListError,
    refetch: refetchPriceList
  } = useGetProductPriceListQuery(
    { productRowId: selectedProductRowId },
    { skip: !isActive || !selectedProductRowId }
  );


  //Mutations
  const [assignToAllPriceLists] = useAssignToAllPriceListsMutation();
  const [getProductPriceListAuditDetails] = useLazyGetProductPriceListAuditDetailsQuery();


  // LOV Data
  const { getByType, isReady: isLovReady } = useLovData();
  const priceListTaxOptions = useMemo(() => {
    if (!isLovReady) return [];
    const taxData = getByType(PRODUCT_PRICE_LIST_TAX_LOV_TYPES.PRODUCT_PRICE_LIST_TAX);
    return taxData.map(item => ({ value: item.rowId, label: item.name }));
  }, [getByType, isLovReady]);

  const refreshPriceListType = useMemo(() => {
    if (!isLovReady) return [];
    const priceListTypeData = getByType(PRODUCT_REFRESH_PRICE_LIST_TYPE_LOV_TYPES.REFRESH_PRICE_LIST_TYPE);
    return priceListTypeData.map(item => ({ value: item.rowId, label: item.name, type: item.type }));
  } , [getByType, isLovReady]);


  // Derived states
  const isInitialLoading = isProductPriceListLoading;
  const isRefreshing = isProductPriceListFetching && !isProductPriceListLoading;
  const priceListData: ProductPriceListItem[] = productPriceListData?.data || [];
  const hasData = priceListData.length > 0;


  // Handle timestamp extraction
  useEffect(() => {
    if (productPriceListData?.data) {
      const timestamps: { [rowId: number]: string } = {};
      productPriceListData.data.forEach((item: ProductPriceListItem) => {
        timestamps[item.rowId] = item.updated;
      });
      setInitialUpdatedTimestamps(timestamps);
    }
  }, [productPriceListData]);

  // Handle API errors
  useEffect(() => {
    if (productPriceListError) {
      console.error('Error fetching product price list:', productPriceListError);
      toast.error('Failed to load product price list. Please try again.');
    }
  }, [productPriceListError]);

  // Actions
  const handleAssignToAllPriceLists = async () => {
    try {
      await assignToAllPriceLists({ productRowId: selectedProductRowId }).unwrap();
      toast.success('Product assigned to all price lists successfully');
      refetchPriceList();
    } catch (error) {
      toast.error('Failed to assign product to all price lists');
    }
  };

  const resetInitialTimestamps = () => {
    setInitialUpdatedTimestamps({});
  };

  // Return everything the components need
  return {
    // Data
    priceListData,
    priceListTaxOptions,
    refreshPriceListType,
    initialUpdatedTimestamps,

    // Loading states
    isInitialLoading,
    isRefreshing,
    hasData,

    // Error state
    productPriceListError,

    // Actions
    refetchPriceList,
    handleAssignToAllPriceLists,
    resetInitialTimestamps,
    getProductPriceListAuditDetails,

    // State setters (for other hooks to use)
    setInitialUpdatedTimestamps
  };

};